import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { formatJPY } from "@juro/shared";
import { Modal } from "../Modal.js";
import { btnStyle } from "../../App.js";
import { toast } from "../Toast.js";
import { workItemsApi, type WorkItemsSummary } from "../../api/workItemsApi.js";
import { documentsApi, type DocumentSummary } from "../../api.js";

type Props = {
  caseId:    string;
  summary:   WorkItemsSummary;
  onClose:   () => void;
  /** 仕上げるべき下書きの一覧（仕上げ順）。呼び出し側でキューを張って先頭へ遷移する */
  onSuccess: (docIds: string[]) => void;
};

const DOC_TYPE_LABEL: Record<string, string> = {
  judicial_invoice:       "司法書士請求書",
  administrative_invoice: "行政書士請求書",
  surveyor_invoice:       "調査士請求書",
  estimate:               "見積書",
};

const PROF_TO_DOC: Record<string, string> = {
  judicial:       "judicial_invoice",
  administrative: "administrative_invoice",
  surveyor:       "surveyor_invoice",
  common:         "judicial_invoice",
};

const SKIP_REASON_LABEL: Record<string, string> = {
  UNSUPPORTED_DOC_TYPE:            "未対応の書類種別です",
  NO_WORK_ITEMS:                   "対象になる実施業務がありません",
  DOCUMENT_DRAFT_EXISTS:           "同じ種類の下書きが既にあります",
  DOCUMENT_ISSUED_REQUIRES_REVISE: "発行済みなので新バージョンを発行します",
  NO_ISSUED_DOCUMENT_TO_VERSION:   "発行済み書類がないため新バージョンを作れません",
};

/**
 * 仕上げ順（そのまま巡回ナビの順序になる）。
 * 見積書は3業種の合算なので最後に置く。
 *
 * 注: 土地計算書・建物計算書・保証協会請求書は今回のバージョンでは作成対象外
 * （docs/design/document-create-flow.md §2.5 参照）。
 */
const ALL_DOC_TYPES = [
  "judicial_invoice",
  "administrative_invoice",
  "surveyor_invoice",
  "estimate",
];

export function GenerateDocumentsModal({ caseId, summary, onClose, onSuccess }: Props) {
  const navigate = useNavigate();

  // ── 既存書類を取得 ──────────────────────────────────────────────────────────
  const { data: docsData } = useQuery({
    queryKey: ["case", caseId, "documents"],
    queryFn:  () => documentsApi.listByCase(caseId),
  });

  // doc_type → 最新の draft ドキュメント
  const existingDraftMap = useMemo(() => {
    const map = new Map<string, DocumentSummary>();
    (docsData?.data ?? [])
      .filter((d) => d.status === "draft")
      .forEach((d) => {
        // 同じ doc_type で複数 draft がある場合は version が最大のものを使う
        const existing = map.get(d.doc_type);
        if (!existing || d.version > existing.version) map.set(d.doc_type, d);
      });
    return map;
  }, [docsData]);

  // ── 使用可能な書類タイプを判定 ──────────────────────────────────────────────
  const availableDocTypes = useMemo(() => {
    const s = new Set<string>();
    Object.keys(summary.by_profession).forEach((prof) => {
      if ((summary.by_profession[prof]?.item_count ?? 0) > 0) {
        const dt = PROF_TO_DOC[prof];
        if (dt) s.add(dt);
      }
    });
    const hasAny = Object.values(summary.by_profession).some((i) => i.item_count > 0);
    if (hasAny) s.add("estimate");
    return s;
  }, [summary]);

  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>(
    Array.from(availableDocTypes),
  );
  const [mode, setMode] = useState<"create" | "replace_draft" | "new_version">("create");
  const [dryRunResult, setDryRunResult] = useState<string | null>(null);

  // ── 下書きが存在するときはモードを自動で「置き換える」に切り替える ─────────────
  useEffect(() => {
    const hasDraft = selectedDocTypes.some((dt) => existingDraftMap.has(dt));
    if (hasDraft) {
      setMode("replace_draft");
    } else {
      setMode("create");
    }
    setDryRunResult(null);
  }, [selectedDocTypes, existingDraftMap]);

  // 選択中の書類のうち下書きがあるもの（警告バナー・モード推奨）
  const selectedWithDraft = selectedDocTypes.filter((dt) => existingDraftMap.has(dt));
  const hasDraftConflict  = selectedWithDraft.length > 0;

  // ── ミューテーション ──────────────────────────────────────────────────────

  /**
   * 仕上げ順（ALL_DOC_TYPES 順）に並べた書類ID。
   * 「新規作成されたもの」だけでなく「既存下書きがあってスキップされたもの」も含める。
   * これが無いと、下書きが既にある書類が一括作成のたびに導線から漏れてしまう。
   */
  function buildQueue(entries: { doc_type: string; document_id: string }[]): string[] {
    const byType = new Map<string, string>();
    entries.forEach((e) => { if (!byType.has(e.doc_type)) byType.set(e.doc_type, e.document_id); });
    return ALL_DOC_TYPES.flatMap((dt) => {
      const id = byType.get(dt);
      return id ? [id] : [];
    });
  }

  /** work-items API のスキップのうち、既存下書きへ誘導できるものを拾う */
  function draftEntriesFromSkips(skipped: { doc_type: string; reason: string }[]) {
    return skipped.flatMap((s) => {
      const existing = existingDraftMap.get(s.doc_type);
      return existing ? [{ doc_type: s.doc_type, document_id: existing.id }] : [];
    });
  }

  const dryRunMut = useMutation({
    mutationFn: () => (selectedDocTypes.length > 0
      ? workItemsApi.generate(caseId, { doc_types: selectedDocTypes, mode, dry_run: true })
      : Promise.resolve({ generated: [], skipped: [], warnings: [] })),
    onSuccess: (res) => {
      const lines = [
        ...res.generated.map((g) => `✅ ${DOC_TYPE_LABEL[g.doc_type] ?? g.doc_type}: ${formatJPY(g.totals.total)}`),
        ...res.skipped.map((s)  => `⏭ ${DOC_TYPE_LABEL[s.doc_type] ?? s.doc_type}: ${SKIP_REASON_LABEL[s.reason] ?? s.reason}`),
        ...res.warnings.map((w) => `⚠️ ${w.message}`),
      ];
      setDryRunResult(lines.join("\n"));
    },
    onError: (e) => toast(e.message, "error"),
  });

  const generateMut = useMutation({
    mutationFn: () => (selectedDocTypes.length > 0
      ? workItemsApi.generate(caseId, { doc_types: selectedDocTypes, mode, dry_run: false })
      : Promise.resolve({ generated: [], skipped: [], warnings: [] })),
    onSuccess: async (wiRes) => {
      const issuedRequiresNewVersion = wiRes.skipped.filter(
        (s) => s.reason === "DOCUMENT_ISSUED_REQUIRES_REVISE",
      );

      if (issuedRequiresNewVersion.length > 0) {
        const docTypes = issuedRequiresNewVersion.map((s) => s.doc_type);
        const labels = docTypes.map((dt) => DOC_TYPE_LABEL[dt] ?? dt).join("・");
        setMode("new_version");
        setDryRunResult(`${labels}: 発行済みなので新バージョンを発行します`);

        try {
          const retry = await workItemsApi.generate(caseId, {
            doc_types: docTypes,
            mode: "new_version",
            dry_run: false,
          });

          const queue = buildQueue([
            ...wiRes.generated,
            ...retry.generated,
            ...draftEntriesFromSkips(retry.skipped),
          ]);

          if (queue.length > 0) {
            toast(buildResultMessage(wiRes.generated.length + retry.generated.length, queue.length));
            onSuccess(queue);
            return;
          }

          const details = retry.skipped
            .map((s) => `${DOC_TYPE_LABEL[s.doc_type] ?? s.doc_type}: ${SKIP_REASON_LABEL[s.reason] ?? s.reason}`)
            .join("\n");
          toast(details || "新バージョンを作成できませんでした", "error");
          if (details) setDryRunResult(details);
        } catch (e) {
          toast((e as Error).message, "error");
        }
        return;
      }

      // 新規生成分に加え、既存下書きでスキップされた書類も仕上げ対象に含める
      const queue = buildQueue([
        ...wiRes.generated,
        ...draftEntriesFromSkips(wiRes.skipped),
      ]);
      const createdCount = wiRes.generated.length;

      if (queue.length === 0) {
        const details = wiRes.skipped
          .map((s) => `${DOC_TYPE_LABEL[s.doc_type] ?? s.doc_type}: ${SKIP_REASON_LABEL[s.reason] ?? s.reason}`)
          .join("\n");
        toast(details || "生成できる書類がありませんでした", "error");
        if (details) setDryRunResult(details);
        return;
      }

      toast(buildResultMessage(createdCount, queue.length));
      onSuccess(queue);
    },
    onError: (e) => toast(e.message, "error"),
  });

  function toggleDocType(dt: string) {
    setSelectedDocTypes((prev) =>
      prev.includes(dt) ? prev.filter((d) => d !== dt) : [...prev, dt],
    );
  }

  return (
    <Modal title="実施業務から書類を作成" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 380 }}>

        {/* 下書き競合の警告バナー */}
        {hasDraftConflict && (
          <div style={{
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #fcd34d",
            background: "#fffbeb",
            fontSize: 12,
            color: "#92400e",
          }}>
            <strong>⚠️ 既存の下書きがあります</strong><br />
            {selectedWithDraft.map((dt) => DOC_TYPE_LABEL[dt] ?? dt).join("・")} に下書きが存在するため、
            <strong>「下書きを置き換える」</strong> を自動で選択しました。
            既存の下書きを残したい場合は「開く」で内容を確認してください。
          </div>
        )}

        {/* 対象書類の選択 */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 2 }}>
            対象書類
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.5 }}>
            選んだ書類を上から順に開いて、体裁を整えながら1件ずつ発行できます。
          </div>
          {ALL_DOC_TYPES.map((dt) => {
            const available   = availableDocTypes.has(dt);
            const checked     = selectedDocTypes.includes(dt);
            const draftDoc    = existingDraftMap.get(dt);
            const hasDraft    = !!draftDoc;

            return (
              <div key={dt} style={{
                display: "flex", flexDirection: "column", gap: 3,
                padding: "7px 10px",
                borderRadius: 7,
                background: checked ? (hasDraft ? "#fffbeb" : "#f0fdfa") : "#f8fafc",
                border: `1px solid ${checked ? (hasDraft ? "#fcd34d" : "#99f6e4") : "#e2e8f0"}`,
                marginBottom: 5,
                opacity: available ? 1 : 0.45,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!available}
                    onChange={() => toggleDocType(dt)}
                    style={{ cursor: available ? "pointer" : "not-allowed", flexShrink: 0 }}
                  />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, cursor: available ? "pointer" : "not-allowed" }}
                        onClick={() => available && toggleDocType(dt)}>
                    {DOC_TYPE_LABEL[dt]}
                  </span>
                  {hasDraft && checked && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/documents/${draftDoc!.id}`); }}
                      style={{
                        fontSize: 11, color: "#0f766e", background: "none", border: "1px solid #99f6e4",
                        borderRadius: 4, padding: "2px 7px", cursor: "pointer", flexShrink: 0,
                      }}
                    >
                      下書きを開く
                    </button>
                  )}
                  {!hasDraft && (
                    <span style={{ fontSize: 11, color: available ? "#64748b" : "#94a3b8", flexShrink: 0 }}>
                      {available ? getDocTypeSummary(dt, summary) : "該当業務なし"}
                    </span>
                  )}
                  {hasDraft && (
                    <span style={{ fontSize: 11, color: "#92400e", flexShrink: 0 }}>
                      {getDocTypeSummary(dt, summary)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* モード選択 */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>
            既存書類への扱い
          </div>
          {[
            { value: "create",        label: "新しい下書きを作る",      desc: "既存の下書きがない場合のみ作成します" },
            { value: "replace_draft", label: "下書きを置き換える",      desc: "既存の下書き明細を実施業務の内容で上書きします" },
            { value: "new_version",   label: "発行済みなら新バージョン", desc: "発行済み書類の修正版（v2, v3…）を作成します" },
          ].map(({ value, label, desc }) => {
            const isCurrent = mode === value;
            const isRecommended = hasDraftConflict && value === "replace_draft";
            return (
              <label key={value} style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "7px 10px",
                borderRadius: 7,
                background: isCurrent ? "#f0fdfa" : "#f8fafc",
                border: `1px solid ${isCurrent ? "#99f6e4" : "#e2e8f0"}`,
                marginBottom: 5,
                cursor: "pointer",
              }}>
                <input
                  type="radio"
                  name="mode"
                  value={value}
                  checked={isCurrent}
                  onChange={() => { setMode(value as typeof mode); setDryRunResult(null); }}
                  style={{ marginTop: 2, cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
                    {isRecommended && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#92400e", background: "#fef9c3", padding: "1px 6px", borderRadius: 4 }}>
                        自動選択
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{desc}</div>
                </div>
              </label>
            );
          })}
        </div>

        {/* プレビュー結果 */}
        {dryRunResult && (
          <div style={{
            background: "#f0fdfa", border: "1px solid #99f6e4",
            borderRadius: 8, padding: 12, fontSize: 12,
            whiteSpace: "pre-line", color: "#134e4a",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>プレビュー結果</div>
            {dryRunResult}
          </div>
        )}

        {/* フッター */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button style={btnStyle("outline")} onClick={onClose}>
            キャンセル
          </button>
          <button
            style={btnStyle("outline")}
            onClick={() => dryRunMut.mutate()}
            disabled={dryRunMut.isPending || selectedDocTypes.length === 0}>
            {dryRunMut.isPending ? "確認中…" : "プレビュー"}
          </button>
          <button
            style={btnStyle("primary")}
            onClick={() => generateMut.mutate()}
            disabled={generateMut.isPending || selectedDocTypes.length === 0}>
            {generateMut.isPending ? "生成中…" : hasDraftConflict ? "置き換えて生成" : "生成して1件目へ"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── helper ────────────────────────────────────────────────────────────────

/** 「N件作成 / 全M件を仕上げます」。既存下書きを巻き込んだ場合に件数が食い違うため両方出す */
function buildResultMessage(createdCount: number, queueLength: number): string {
  if (createdCount === 0) {
    return `既存の下書き${queueLength}件を順番に仕上げます`;
  }
  if (createdCount === queueLength) {
    return `${createdCount}件の書類を作成しました。順番に仕上げます`;
  }
  return `${createdCount}件を作成しました。既存の下書きを含む${queueLength}件を順番に仕上げます`;
}

function getDocTypeSummary(dt: string, summary: WorkItemsSummary): string {
  const { by_profession, totals } = summary;
  if (dt === "estimate") return formatJPY(totals.grand_total);
  const profMap: Record<string, string[]> = {
    judicial_invoice:       ["judicial", "common"],
    administrative_invoice: ["administrative"],
    surveyor_invoice:       ["surveyor"],
  };
  const profs = profMap[dt] ?? [];
  const fee   = profs.reduce((s, p) => s + (by_profession[p]?.fee_subtotal ?? 0), 0);
  const pass  = profs.reduce((s, p) => s + (by_profession[p]?.pass_through  ?? 0), 0);
  return formatJPY(fee + pass);
}
